import { createFileRoute, Link } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { z } from "zod";
import { Upload, FileVideo, X, CheckCircle2, Sparkles, ShieldCheck, Trophy, Menu, Users, Award, PlayCircle, Search, BarChart3, GraduationCap, Shield } from "lucide-react";
import { BrandLogo } from "@/projects/components/brand-logo";
import { Button } from "@/projects/components/ui/button";
import { Input } from "@/projects/components/ui/input";
import { Label } from "@/projects/components/ui/label";
import { Progress } from "@/projects/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/projects/components/ui/dialog";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/projects/components/ui/sheet";
import { toast } from "sonner";
import { addSubmission, type Submission } from "@/projects/lib/store";
import { cn } from "@/projects/lib/utils";


export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "રાજનગર જ્ઞાનોત્સવ 2.0 — Submit Your Educational Video" },
      { name: "description", content: "Rajnagar Gyanotsav 2.0 — Share your knowledge and showcase your talent by submitting an educational video." },
      { property: "og:title", content: "રાજનગર જ્ઞાનોત્સવ 2.0 — Submit Your Educational Video" },
      { property: "og:description", content: "Rajnagar Gyanotsav 2.0 — Share your knowledge and showcase your talent by submitting an educational video." },
    ],
  }),
  component: LandingPage,
});

const MAX_MB = 100;
const ALLOWED = ["video/mp4", "video/quicktime", "video/x-msvideo", "video/webm", "video/avi"];

const schema = z.object({
  studentName: z.string().trim().min(1, "Student name is required").max(100),
  studentId: z.string().trim().min(1, "Student ID is required").max(50),
  videoTitle: z.string().trim().min(1, "Video title is required").max(150),
});

function LandingPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <Hero />
      <StatsMarquee />
      <FeatureStrip />
      <section id="submit" className="scroll-mt-20 py-14 lg:py-24">
        <div className="mx-auto max-w-3xl px-4">
          <div className="mb-8 text-center animate-rise">
            <div className="inline-flex items-center gap-2 rounded-full border border-saffron/40 bg-saffron-soft px-3 py-1 text-xs font-medium text-saffron">
              <Sparkles className="h-3.5 w-3.5" /> Open for submissions
            </div>
            <h2 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">Student Video Submission Form</h2>
            <p className="mt-2 text-sm text-muted-foreground sm:text-base">Fill in your details and upload your educational video for review.</p>
          </div>
          <SubmissionForm />
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}

function SiteHeader() {
  const [open, setOpen] = useState(false);
  const features: Array<{ to: string; label: string; icon: typeof Upload; tone: "saffron" | "royal" | "gold"; badge?: string; isHash?: boolean; cta?: boolean }> = [
    { to: "#submit", label: "Submit Video", icon: Upload, tone: "saffron", badge: "Open", isHash: true },
    
    { to: "/login/teacher", label: "Teacher Panel", icon: GraduationCap, tone: "royal" },
    { to: "/login/admin", label: "Admin Panel", icon: Shield, tone: "gold", cta: true },
  ];
  return (
    <header className="sticky top-0 z-40 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/70">
      {/* Announcement strip */}
      <div className="hidden bg-royal text-white md:block">
        <div className="mx-auto flex h-8 max-w-7xl items-center gap-4 px-4 text-[11px] font-medium">
          <span className="inline-flex items-center gap-1.5 text-gold">
            <Sparkles className="h-3 w-3" /> Season 2.0 · 2026 is live
          </span>
          <span className="h-3 w-px bg-white/20" />
          <span className="inline-flex items-center gap-1.5 text-white/85"><Upload className="h-3 w-3" /> Video submissions open</span>
          <span className="inline-flex items-center gap-1.5 text-white/85"><ShieldCheck className="h-3 w-3" /> Teacher verified</span>
          <span className="inline-flex items-center gap-1.5 text-white/85"><BarChart3 className="h-3 w-3" /> Live analytics</span>
          <span className="ml-auto text-white/70">Share · Learn · Shine</span>
        </div>
      </div>

      <div className="mx-auto flex h-16 max-w-7xl items-center gap-3 px-4">
        <Link to="/" className="flex min-w-0 items-center gap-2.5">
          <BrandLogo className="h-9 w-9 sm:h-10 sm:w-10" />
          <div className="min-w-0">
            <div className="truncate text-guj text-sm font-bold leading-tight sm:text-base">રાજનગર જ્ઞાનોત્સવ 2.0</div>
            <div className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">Educational Event</div>
          </div>
        </Link>
        <div className="flex-1" />
        <nav className="hidden items-center gap-1 lg:flex">
          {features.map((f) => {
            const Icon = f.icon;
            const toneRing =
              f.tone === "saffron" ? "hover:border-saffron/50 hover:bg-saffron-soft hover:text-saffron"
              : f.tone === "gold" ? "border-gold/60 bg-gold/10 text-foreground hover:bg-gold/20"
              : "hover:border-royal/40 hover:bg-royal/5 hover:text-royal";
            const inner = (
              <span className={cn(
                "group relative inline-flex items-center gap-1.5 rounded-full border border-transparent px-3 py-1.5 text-sm font-medium transition-all",
                toneRing,
                f.cta && "shadow-royal",
              )}>
                <Icon className="h-4 w-4" />
                {f.label}
                {"badge" in f && f.badge && (
                  <span className="ml-1 rounded-full bg-saffron px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white">
                    {f.badge}
                  </span>
                )}
              </span>
            );
            return "isHash" in f && f.isHash ? (
              <a key={f.label} href={f.to}>{inner}</a>
            ) : (
              <Link key={f.label} to={f.to}>{inner}</Link>
            );
          })}
        </nav>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button size="icon" variant="ghost" className="lg:hidden" aria-label="Open menu">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-80">
            <SheetHeader>
              <SheetTitle className="flex items-center gap-2 text-guj">
                <BrandLogo className="h-8 w-8" /> રાજનગર જ્ઞાનોત્સવ 2.0
              </SheetTitle>
            </SheetHeader>
            <div className="mt-2 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">Features</div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {features.map((f) => {
                const Icon = f.icon;
                const cls = "flex flex-col items-start gap-1 rounded-xl border p-3 text-sm font-medium hover:border-royal/40 hover:bg-royal/5";
                const inner = (
                  <>
                    <span className={cn(
                      "grid h-8 w-8 place-items-center rounded-lg",
                      f.tone === "saffron" ? "bg-saffron-soft text-saffron" : f.tone === "gold" ? "bg-gold/20 text-foreground" : "bg-royal/10 text-royal",
                    )}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <span>{f.label}</span>
                    {"badge" in f && f.badge && (
                      <span className="rounded-full bg-saffron px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">{f.badge}</span>
                    )}
                  </>
                );
                return "isHash" in f && f.isHash ? (
                  <a key={f.label} href={f.to} onClick={() => setOpen(false)} className={cls}>{inner}</a>
                ) : (
                  <Link key={f.label} to={f.to} onClick={() => setOpen(false)} className={cls}>{inner}</Link>
                );
              })}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section id="top" className="relative overflow-hidden border-b gradient-royal text-white">
      {/* Elegant gold-rays + royal depth layer */}
      <div className="pointer-events-none absolute inset-0 rays-bg" />
      {/* Cultural dot motif */}
      <div className="absolute inset-0 motif-bg opacity-70" />
      {/* Vignette for depth */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_50%,oklch(0.18_0.08_265/0.55)_100%)]" />

      <div className="relative mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:py-24 lg:grid-cols-2 lg:py-32">

        <div className="flex flex-col justify-center animate-rise">
          <div className="inline-flex w-fit items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3.5 py-1.5 text-xs font-semibold uppercase tracking-widest text-gold backdrop-blur">
            <Trophy className="h-3.5 w-3.5" /> Season 2.0 · 2026
          </div>
          <h1 className="text-guj mt-5 text-4xl font-extrabold leading-[1.02] drop-shadow-[0_2px_20px_oklch(0.18_0.08_265/0.6)] sm:text-5xl lg:text-6xl xl:text-7xl">
            રાજનગર
            <br />
            જ્ઞાનોત્સવ <span className="text-gradient-gold">2.0</span>
          </h1>
          <div className="mt-5 flex items-center gap-3">
            <span className="h-px w-10 bg-gold/70" />
            <p className="text-sm font-medium uppercase tracking-[0.22em] text-gold/90">Share · Learn · Shine</p>
          </div>
          <p className="mt-4 max-w-xl text-base text-white/85 sm:text-lg lg:text-xl">
            Share Your Knowledge, Showcase Your Talent.
          </p>
          <p className="mt-3 max-w-xl text-sm text-white/70 sm:text-base">
            A celebration of learning where students submit educational videos, teachers verify quality, and the best get recognized.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#submit">
              <Button size="lg" className="bg-gold text-primary hover:bg-gold/90 sheen-on-hover shadow-royal font-semibold">
                <Upload className="mr-2 h-4 w-4" /> Submit Your Video
              </Button>
            </a>
            <a href="#features">
              <Button size="lg" variant="outline" className="border-white/30 bg-white/5 text-white backdrop-blur hover:bg-white/15 hover:text-white">
                <PlayCircle className="mr-2 h-4 w-4" /> How it works
              </Button>
            </a>
          </div>
        </div>

        {/* Hero art card */}
        <div className="relative hidden items-center justify-center lg:flex">
          <div className="absolute inset-6 rounded-full border border-gold/25 animate-spin-slow" aria-hidden />
          <div className="absolute inset-14 rounded-full border border-white/10 animate-spin-slow [animation-direction:reverse] [animation-duration:40s]" aria-hidden />
          <div className="absolute inset-24 rounded-full border border-gold/15" aria-hidden />
          <div className="relative aspect-[4/3] w-full max-w-md rounded-2xl border border-gold/30 bg-white/[0.06] p-3 shadow-royal backdrop-blur-md animate-soft-float">
            <div className="grid h-full w-full place-items-center overflow-hidden rounded-xl bg-gradient-to-br from-white/15 to-white/[0.02]">
              <div className="text-center">
                <div className="mx-auto grid h-24 w-24 place-items-center rounded-full bg-gold text-primary shadow-royal animate-glow">
                  <FileVideo className="h-12 w-12" />
                </div>
                <div className="mt-5 text-guj text-xl font-bold text-gold">વિદ્યા દાનમ્ પરમમ્ દાનમ્</div>
                <div className="mt-1 text-sm text-white/80">Knowledge shared is knowledge multiplied</div>
              </div>
            </div>
            <div className="absolute -bottom-4 -left-4 rounded-xl border bg-background p-3 text-foreground shadow-card animate-soft-float [animation-delay:-2s]">
              <div className="flex items-center gap-2 text-xs font-medium">
                <div className="grid h-8 w-8 place-items-center rounded-full bg-success/10 text-success">
                  <CheckCircle2 className="h-4 w-4" />
                </div>
                Ready for Reward · +214
              </div>
            </div>
            <div className="absolute -right-4 -top-4 rounded-xl border bg-background p-3 text-foreground shadow-card animate-soft-float">
              <div className="flex items-center gap-2 text-xs font-medium">
                <div className="grid h-8 w-8 place-items-center rounded-full bg-royal-soft text-royal">
                  <Users className="h-4 w-4" />
                </div>
                500+ Students
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Elegant gold divider at bottom */}
      <div className="relative h-px w-full bg-gradient-to-r from-transparent via-gold/60 to-transparent" />
    </section>
  );
}

function StatsMarquee() {
  const items = [
    { icon: Users, label: "500+ Students" },
    { icon: FileVideo, label: "1,200+ Videos" },
    { icon: ShieldCheck, label: "Teacher Verified" },
    { icon: Award, label: "Best of Best Rewards" },
    { icon: Sparkles, label: "Season 2.0 · 2026" },
  ];
  const row = [...items, ...items];
  return (
    <div className="border-b bg-gradient-to-r from-royal-soft via-background to-saffron-soft/60 py-3">
      <div className="mask-fade relative overflow-hidden">
        <div className="flex w-max animate-marquee gap-10 px-4">
          {row.map((it, i) => (
            <div key={i} className="flex shrink-0 items-center gap-2 text-sm font-medium text-royal">
              <it.icon className="h-4 w-4 text-saffron" />
              <span>{it.label}</span>
              <span className="text-muted-foreground/40">✦</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FeatureStrip() {
  const items = [
    { icon: Upload, title: "Easy Uploads", text: "Drag & drop videos up to 100 MB.", tone: "bg-royal-soft text-royal" },
    { icon: ShieldCheck, title: "Teacher Verified", text: "Every entry reviewed by subject teachers.", tone: "bg-saffron-soft text-saffron" },
    { icon: Trophy, title: "Rewards", text: "Best submissions earn recognition.", tone: "bg-gold-soft text-gold" },
  ];
  return (
    <section id="features" className="border-b bg-muted/30 py-10 sm:py-14">
      <div className="mx-auto grid max-w-7xl gap-4 px-4 sm:grid-cols-3">
        {items.map((i, idx) => (
          <div
            key={i.title}
            style={{ animationDelay: `${idx * 120}ms` }}
            className="group hover-lift animate-rise flex items-start gap-3 rounded-2xl border bg-card p-4 shadow-soft sm:p-5"
          >
            <div className={`grid h-11 w-11 shrink-0 place-items-center rounded-xl transition-transform group-hover:scale-110 ${i.tone}`}>
              <i.icon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="font-semibold">{i.title}</div>
              <div className="text-sm text-muted-foreground">{i.text}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}


function SubmissionForm() {
  const [studentName, setStudentName] = useState("");
  const [studentId, setStudentId] = useState("");
  const [videoTitle, setVideoTitle] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const [receipt, setReceipt] = useState<Submission | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const validateFile = (f: File): string | null => {
    if (f.size > MAX_MB * 1024 * 1024) return `File exceeds ${MAX_MB} MB limit.`;
    const ext = f.name.split(".").pop()?.toLowerCase();
    const okExt = ["mp4", "mov", "avi", "webm"].includes(ext ?? "");
    if (!ALLOWED.includes(f.type) && !okExt) return "Only MP4, MOV, AVI or WebM videos are allowed.";
    return null;
  };

  const handleFile = (f: File | null) => {
    if (!f) { setFile(null); return; }
    const err = validateFile(f);
    if (err) {
      toast.error(err);
      setErrors((e) => ({ ...e, file: err }));
      return;
    }
    setErrors((e) => { const { file: _, ...rest } = e; return rest; });
    setFile(f);
    // Simulate upload progress
    setUploading(true);
    setProgress(0);
    const t = setInterval(() => {
      setProgress((p) => {
        if (p >= 100) { clearInterval(t); setUploading(false); return 100; }
        return p + 10;
      });
    }, 120);
  };

  const reset = () => {
    setStudentName(""); setStudentId(""); setVideoTitle(""); setFile(null);
    setErrors({}); setProgress(0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = schema.safeParse({ studentName, studentId, videoTitle });
    const newErrors: Record<string, string> = {};
    if (!result.success) {
      for (const iss of result.error.issues) newErrors[String(iss.path[0])] = iss.message;
    }
    if (!file) newErrors.file = "Please upload a video file.";
    setErrors(newErrors);
    if (Object.keys(newErrors).length) return;

    setSaving(true);
    await new Promise((r) => setTimeout(r, 700));
    const sub = addSubmission({
      studentName: studentName.trim(),
      studentId: studentId.trim(),
      videoTitle: videoTitle.trim(),
      fileName: file!.name,
      fileSizeMB: Math.round((file!.size / (1024 * 1024)) * 10) / 10,
    });
    setSaving(false);
    setReceipt(sub);
    toast.success("Submission saved successfully");
    reset();
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="rounded-2xl border bg-card p-6 shadow-card sm:p-8">
        <div className="grid gap-5">
          <div>
            <Label htmlFor="name">Student Name *</Label>
            <Input id="name" maxLength={100} placeholder="Enter your full name"
              value={studentName} onChange={(e) => setStudentName(e.target.value)} />
            {errors.studentName && <p className="mt-1 text-xs text-destructive">{errors.studentName}</p>}
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <Label htmlFor="sid">Student ID *</Label>
              <Input id="sid" maxLength={50} placeholder="Enter your Student ID"
                value={studentId} onChange={(e) => setStudentId(e.target.value)} />
              {errors.studentId && <p className="mt-1 text-xs text-destructive">{errors.studentId}</p>}
            </div>
            <div>
              <Label htmlFor="title">Video Title *</Label>
              <Input id="title" maxLength={150} placeholder="Enter video title"
                value={videoTitle} onChange={(e) => setVideoTitle(e.target.value)} />
              {errors.videoTitle && <p className="mt-1 text-xs text-destructive">{errors.videoTitle}</p>}
            </div>
          </div>

          <div>
            <Label>Video Attachment *</Label>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault(); setDragging(false);
                const f = e.dataTransfer.files?.[0]; if (f) handleFile(f);
              }}
              className={cn(
                "mt-1 flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-6 text-center transition-colors",
                dragging ? "border-primary bg-royal-soft" : "border-border bg-muted/30",
                errors.file && "border-destructive/60 bg-destructive/5",
              )}
            >
              {!file ? (
                <>
                  <div className="grid h-12 w-12 place-items-center rounded-full bg-royal-soft text-royal">
                    <Upload className="h-5 w-5" />
                  </div>
                  <div className="mt-3 text-sm font-medium">Drag & drop your video here</div>
                  <div className="text-xs text-muted-foreground">or</div>
                  <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => inputRef.current?.click()}>
                    Browse Video
                  </Button>
                  <input
                    ref={inputRef} type="file" className="hidden" accept="video/mp4,video/quicktime,video/x-msvideo,video/webm,.mp4,.mov,.avi,.webm"
                    onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
                  />
                </>
              ) : (
                <div className="w-full">
                  <div className="flex items-center justify-between gap-3 rounded-lg border bg-background p-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-royal-soft text-royal">
                        <FileVideo className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">{file.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {(file.size / (1024 * 1024)).toFixed(1)} MB
                        </div>
                      </div>
                    </div>
                    <button type="button" onClick={() => { setFile(null); setProgress(0); }} className="rounded-md p-1.5 text-muted-foreground hover:bg-muted">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  {uploading && <Progress value={progress} className="mt-3 h-1.5" />}
                  <div className="mt-2 flex justify-end">
                    <Button type="button" variant="ghost" size="sm" onClick={() => inputRef.current?.click()}>Replace</Button>
                    <input
                      ref={inputRef} type="file" className="hidden" accept="video/mp4,video/quicktime,video/x-msvideo,video/webm,.mp4,.mov,.avi,.webm"
                      onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
                    />
                  </div>
                </div>
              )}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">Maximum allowed video size is 100 MB. Formats: MP4, MOV, AVI, WebM.</p>
            {errors.file && <p className="mt-1 text-xs text-destructive">{errors.file}</p>}
          </div>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={reset}>Reset</Button>
            <Button type="submit" disabled={saving || uploading} className="min-w-40">
              {saving ? "Saving..." : "Save Submission"}
            </Button>
          </div>
        </div>
      </form>

      <Dialog open={!!receipt} onOpenChange={(o) => !o && setReceipt(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="mx-auto mb-2 grid h-14 w-14 place-items-center rounded-full bg-success/10 text-success">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <DialogTitle className="text-center text-2xl">Thank You!</DialogTitle>
            <DialogDescription className="text-center">
              Your video has been submitted successfully for verification.
            </DialogDescription>
          </DialogHeader>
          {receipt && (
            <div className="mt-2 space-y-2 rounded-lg border bg-muted/40 p-4 text-sm">
              <Row k="Student Name" v={receipt.studentName} />
              <Row k="Student ID" v={receipt.studentId} />
              <Row k="Video Title" v={receipt.videoTitle} />
              <Row k="Submitted" v={new Date(receipt.submittedAt).toLocaleString()} />
              <Row k="Reference #" v={<span className="font-mono font-semibold text-royal">{receipt.id}</span>} />
            </div>
          )}
          <div className="mt-2 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={() => setReceipt(null)}>Close</Button>
            <Button onClick={() => setReceipt(null)}>Submit Another Video</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="text-muted-foreground">{k}</div>
      <div className="font-medium text-foreground">{v}</div>
    </div>
  );
}

function SiteFooter() {
  return (
    <footer className="border-t bg-muted/30 py-8">
      <div className="mx-auto max-w-7xl px-4 text-center text-sm text-muted-foreground">
        <div className="text-guj font-semibold text-foreground">રાજનગર જ્ઞાનોત્સવ 2.0</div>
        <div className="mt-1">© {new Date().getFullYear()} Rajnagar Educational Committee. All rights reserved.</div>
      </div>
    </footer>
  );
}
